/**
 * contentService
 * Firestore data-access layer for core content collections:
 * - `articles`
 * - `animations`
 * - `models`
 *
 * Responsibilities:
 * - validate supported collection keys
 * - normalize timestamps/tags for UI
 * - sanitize payloads before writes
 * - expose CRUD helpers used by public and dashboard pages
 */
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db, assertFirebaseConfigured } from '../lib/firebase/config';
import { CONTENT_KEYS } from '../utils/contentConfig';
import { slugify } from '../utils/slugify';

function assertCollectionKey(collectionKey) {
  if (!CONTENT_KEYS.includes(collectionKey)) {
    throw new Error(`Unsupported collection: ${collectionKey}`);
  }
}

function normalizeTags(tags) {
  if (Array.isArray(tags)) {
    return tags
      .map((tag) => (tag ?? '').toString().trim())
      .filter(Boolean);
  }

  if (typeof tags === 'string') {
    return tags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  }

  return [];
}

function normalizeArticleSections(sections) {
  if (!Array.isArray(sections)) {
    return [];
  }

  return sections
    .map((section) => ({
      contentType: ['Text', 'Link', 'Quote'].includes(section?.contentType)
        ? section.contentType
        : 'Text',
      subHeadline: (section?.subHeadline ?? '').toString().trim(),
      body: (section?.body ?? '').toString().trim(),
      imageUrl: (section?.imageUrl ?? '').toString().trim(),
      imagePath: (section?.imagePath ?? '').toString().trim(),
      imageCaption: (section?.imageCaption ?? '').toString().trim(),
    }))
    .filter(
      (section) =>
        section.subHeadline ||
        section.body ||
        section.imageUrl ||
        section.imagePath ||
        section.imageCaption
    );
}

function normalizeArticleReferences(references) {
  if (!Array.isArray(references)) {
    return [];
  }

  return references
    .map((reference) => ({
      text: (reference?.text ?? '').toString().trim(),
      link: (reference?.link ?? '').toString().trim(),
    }))
    .filter((reference) => reference.text || reference.link);
}

function normalizeArticleFootnotes(footnotes) {
  if (!Array.isArray(footnotes)) {
    return [];
  }

  return footnotes
    .map((footnote, index) => {
      const rawNumber = Number(footnote?.footnoteNumber);
      return {
        footnoteNumber: Number.isFinite(rawNumber) ? rawNumber : index + 1,
        text: (footnote?.text ?? '').toString().trim(),
        link: (footnote?.link ?? '').toString().trim(),
      };
    })
    .filter((footnote) => footnote.text || footnote.link);
}

function toDate(value) {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  if (typeof value.toDate === 'function') {
    return value.toDate();
  }

  return null;
}

function toMillis(value) {
  const date = toDate(value);
  return date ? date.getTime() : 0;
}

function normalizeSortOrder(value) {
  const parsed = Number.parseInt(value, 10);
  if (Number.isFinite(parsed) && parsed > 0) {
    return parsed;
  }

  return null;
}

function sortRankForModels(value) {
  const normalized = normalizeSortOrder(value);
  return normalized ?? Number.MAX_SAFE_INTEGER;
}

function normalizeDocument(snapshot) {
  const data = snapshot.data();
  return {
    id: snapshot.id,
    ...data,
    tags: normalizeTags(data.tags),
    articleSections: normalizeArticleSections(data.articleSections),
    articleReferences: normalizeArticleReferences(data.articleReferences),
    articleFootnotes: normalizeArticleFootnotes(data.articleFootnotes),
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
  };
}

function sanitizePayload(payload, collectionKey = null, options = {}) {
  const { applyDefaults = false } = options;
  const next = { ...payload };
  const hasField = (fieldName) =>
    Object.prototype.hasOwnProperty.call(next, fieldName);

  if (typeof next.title === 'string') {
    next.title = next.title.trim();
  }

  if (typeof next.summary === 'string') {
    next.summary = next.summary.trim();
  }

  if (typeof next.description === 'string') {
    next.description = next.description.trim();
  }

  if (typeof next.body === 'string') {
    next.body = next.body.trim();
  }

  if (typeof next.topic === 'string') {
    next.topic = next.topic.trim();
  }

  if (typeof next.creatorName === 'string') {
    next.creatorName = next.creatorName.trim();
  }

  if (typeof next.creatorDiscipline === 'string') {
    next.creatorDiscipline = next.creatorDiscipline.trim();
  }

  if (typeof next.creatorStatement === 'string') {
    next.creatorStatement = next.creatorStatement.trim();
  }

  if (typeof next.mediaType === 'string') {
    next.mediaType = next.mediaType.trim();
  }

  if (typeof next.author === 'string') {
    next.author = next.author.trim();
  }

  if (typeof next.authorCreatorId === 'string') {
    next.authorCreatorId = next.authorCreatorId.trim();
  }

  if (typeof next.type === 'string') {
    next.type = next.type.trim();
  }

  if (hasField('sortOrder')) {
    next.sortOrder = normalizeSortOrder(next.sortOrder);
  } else if (applyDefaults && collectionKey === 'models') {
    next.sortOrder = null;
  }

  if (hasField('status')) {
    next.status = next.status === 'published' ? 'published' : 'draft';
  } else if (applyDefaults) {
    next.status = 'draft';
  }

  if (hasField('tags')) {
    next.tags = normalizeTags(next.tags);
  } else if (applyDefaults) {
    next.tags = [];
  }

  if (hasField('slug') || hasField('title')) {
    // Canonical slug assignment keeps URLs stable across create/update.
    next.slug = next.slug ? slugify(next.slug) : slugify(next.title ?? '');
  } else if (applyDefaults) {
    next.slug = '';
  }

  if (hasField('articleSections')) {
    next.articleSections = normalizeArticleSections(next.articleSections);
  } else if (applyDefaults && collectionKey === 'articles') {
    next.articleSections = [];
  }

  if (hasField('articleReferences')) {
    next.articleReferences = normalizeArticleReferences(next.articleReferences);
  } else if (applyDefaults && collectionKey === 'articles') {
    next.articleReferences = [];
  }

  if (hasField('articleFootnotes')) {
    next.articleFootnotes = normalizeArticleFootnotes(next.articleFootnotes);
  } else if (applyDefaults && collectionKey === 'articles') {
    next.articleFootnotes = [];
  }

  if (collectionKey === 'articles' && hasField('authorCreatorId')) {
    next.authorCreatorId = (next.authorCreatorId ?? '').toString().trim();
  } else if (collectionKey === 'articles' && applyDefaults) {
    next.authorCreatorId = '';
  }

  if (collectionKey === 'articles' && hasField('author')) {
    next.author = (next.author ?? '').toString().trim() || 'No Author';
  } else if (collectionKey === 'articles' && applyDefaults) {
    next.author = 'No Author';
  }

  return next;
}

export async function listContent(collectionKey, options = {}) {
  assertFirebaseConfigured();
  assertCollectionKey(collectionKey);

  const { publishedOnly = false } = options;
  const baseCollection = collection(db, collectionKey);
  const snapshot = publishedOnly
    ? await getDocs(query(baseCollection, where('status', '==', 'published')))
    : await getDocs(baseCollection);

  return snapshot.docs
    .map(normalizeDocument)
    .sort((a, b) => {
      if (collectionKey === 'models') {
        const orderDifference =
          sortRankForModels(a.sortOrder) - sortRankForModels(b.sortOrder);
        if (orderDifference !== 0) {
          return orderDifference;
        }
      }

      return (
        toMillis(b.updatedAt ?? b.createdAt) -
        toMillis(a.updatedAt ?? a.createdAt)
      );
    });
}

export async function getContentById(collectionKey, id) {
  assertFirebaseConfigured();
  assertCollectionKey(collectionKey);

  const docRef = doc(db, collectionKey, id);
  const snapshot = await getDoc(docRef);
  if (!snapshot.exists()) {
    return null;
  }

  return normalizeDocument(snapshot);
}

export async function createContentDocument(collectionKey, payload, user) {
  assertFirebaseConfigured();
  assertCollectionKey(collectionKey);

  const safePayload = sanitizePayload(payload, collectionKey, { applyDefaults: true });
  const docRef = await addDoc(collection(db, collectionKey), {
    ...safePayload,
    // Lightweight audit metadata for operational tracking.
    createdBy: user?.uid ?? null,
    updatedBy: user?.uid ?? null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return docRef.id;
}

export async function updateContentDocument(collectionKey, id, payload, user) {
  assertFirebaseConfigured();
  assertCollectionKey(collectionKey);

  const safePayload = sanitizePayload(payload, collectionKey, { applyDefaults: false });
  const docRef = doc(db, collectionKey, id);
  await updateDoc(docRef, {
    ...safePayload,
    updatedBy: user?.uid ?? null,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteContentDocument(collectionKey, id) {
  assertFirebaseConfigured();
  assertCollectionKey(collectionKey);

  const docRef = doc(db, collectionKey, id);
  await deleteDoc(docRef);
}

export function collectStoragePaths(contentItem) {
  // Centralized path collection keeps delete flows consistent.
  const directFields = [
    contentItem?.coverImagePath,
    contentItem?.mediaPath,
    contentItem?.videoPath,
    contentItem?.thumbnailPath,
    contentItem?.previewImagePath,
  ];

  const relatedMedia = Array.isArray(contentItem?.relatedMedia)
    ? contentItem.relatedMedia.map((item) => item?.path)
    : [];

  const attachments = Array.isArray(contentItem?.attachments)
    ? contentItem.attachments.map((item) => item?.path)
    : [];

  const sectionImagePaths = Array.isArray(contentItem?.articleSections)
    ? contentItem.articleSections.map((section) => section?.imagePath)
    : [];

  return [...directFields, ...relatedMedia, ...attachments, ...sectionImagePaths].filter(
    Boolean
  );
}
